import {type ReactNode, useRef, useEffect, useState} from 'react'
import {useLocation, useSearchParams, Outlet} from 'react-router-dom'
import plateLoaderGif from '@/assets/plate-loader.gif'
import type { RootState } from '@/store'
import Sidebar from '../Sidebar/Sidebar.tsx'
import MobileSidebar from '../MobileSidebar/MobileSidebar.tsx'
import MobileTopBar from '../MobileSidebar/components/MobileTopBar/MobileTopBar.tsx'
import WorkspaceModalRouter from '../../modals/Modal/WorkspaceModalRouter.tsx'
import useIsMobile from '../../../hooks/useIsMobile.ts'
import css from './WorkspaceLayout.module.css'
import {useAppDispatch, useAppSelector} from "@/store/hooks.ts";
import {selectAuth} from "@/store/auth/authSelectors.ts";
import {setOpenModal} from "@/store/ui/uiSlice";
import type {ModalState} from "@/types/modal";

// Tabs that a `?settings=<tab>` deep link may open (e.g. the Slack OAuth return lands on
// ?settings=integrations). Mirrors the projectSettings initialTab union in types/modal.ts.
type SettingsTab = NonNullable<Extract<ModalState, { type: 'projectSettings' }>['initialTab']>
const SETTINGS_TABS: SettingsTab[] = ['general', 'members', 'workflow', 'apps', 'integrations', 'billing', 'usage']

interface WorkspaceLayoutProps {
    children?: ReactNode
}

const WorkspaceLayout = ({children}: WorkspaceLayoutProps) => {
    const dispatch = useAppDispatch()
    const auth = useAppSelector(selectAuth)
    const creatingProjectId = useAppSelector((state: RootState) => state.projects.creatingProjectId)
    const isMobile = useIsMobile()
    const layoutRef = useRef<HTMLDivElement>(null)
    const { pathname } = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    const [overlayVisible, setOverlayVisible] = useState(false)
    const [overlayHiding, setOverlayHiding] = useState(false)

    useEffect(() => {
        layoutRef.current?.scrollTo(0, 0)
    }, [pathname])

    // Open the workspace-settings modal when a `?settings=<tab>` deep link lands here
    // (e.g. the Slack OAuth return → ?settings=integrations), then strip the param so a
    // refresh/back doesn't reopen it.
    useEffect(() => {
        const tab = searchParams.get('settings')
        if (!tab || !SETTINGS_TABS.includes(tab as SettingsTab)) return
        dispatch(setOpenModal({ type: 'projectSettings', initialTab: tab as SettingsTab }))
        setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            next.delete('settings')
            return next
        }, { replace: true })
    }, [searchParams, dispatch, setSearchParams])

    useEffect(() => {
        if (creatingProjectId) {
            setOverlayVisible(true)
            setOverlayHiding(false)
        } else if (overlayVisible) {
            setOverlayHiding(true)
            const t = setTimeout(() => {
                setOverlayVisible(false)
                setOverlayHiding(false)
            }, 700)
            return () => clearTimeout(t)
        }
    }, [creatingProjectId])

    return (
        <div ref={layoutRef} className={css.layout} data-scroll-container>
            {isMobile ? (
                auth && (
                    <>
                        <div className={css.header}>
                            <MobileTopBar/>
                        </div>
                        <MobileSidebar/>
                    </>
                )
            ) : (
                <div className={css.sidebar}>
                    <Sidebar/>
                </div>
            )}
            <div className={css.content}>
                {children ?? <Outlet />}
            </div>
            <WorkspaceModalRouter/>
            {overlayVisible && (
                <div className={`${css.creating_overlay}${overlayHiding ? ` ${css.creating_overlay_hiding}` : ''}`}>
                    <img src={plateLoaderGif} width={32} height={32} alt="" />
                </div>
            )}
        </div>
    )
}

export default WorkspaceLayout
